/**
 * Draft → create-rail prefill mapping. The AI NEVER books — this only
 * seeds the EXISTING CreateAppointmentPanel form so the rail's own flow
 * (provider → profile → real slot pick → Save) finishes the job:
 *
 *   - `selectedDate` lands the panel's date strip on the parsed day, so
 *     the slot picker fetches that day's REAL availability.
 *   - `recurring` pre-populates the recurrence summary card + seeds the
 *     configurator (`recurrence-picker-view.tsx` reads `existing`); the
 *     configurator's RetrieveSlots call still owns the real occurrence
 *     dates on Save.
 *   - The patient search seed travels separately (`initialPatientQuery`
 *     on CreateAppointmentPanel) because the panel's patient section
 *     owns its search box state — the form only carries a PICKED patient.
 *
 * Deliberately NOT set: slotId/slotStart/slotEnd — those must come from
 * the panel's availability fetch (the Zod schema requires a real slot
 * before Save enables), and providerId/appointmentProfileId — the parse
 * carries only free-text hints, never ids.
 */

import { addMinutesISO } from '@/scheduling/lib/quick-create-slot';
import type { AppointmentFormValues } from '@/types/appointment-form';
import type { SchedulingCommandDraft } from './scheduling-command-stub-parser';

export function buildAiDraftPrefill(
  draft: SchedulingCommandDraft,
): Partial<AppointmentFormValues> {
  const prefill: Partial<AppointmentFormValues> = {};
  if (!draft.start) return prefill;

  /**
   * Carry the parsed WISH TIME, not just the day.
   *
   * This used to truncate to `slice(0, 10)` — a date with no time — which
   * threw away the one thing the user's sentence worked hardest to say. The
   * picker then showed the day's FIRST two slots (8:00, 8:15) to someone who
   * had asked for 10am, and they had to open the full sub-page to find the
   * time they had already named. That was the single largest reason the AI
   * path cost more taps than booking by hand.
   *
   * Safe because the slots query normalises it: `useAvailableSlots` runs
   * `localDayRange()`, which forces setHours(0,0,0,0) — the WHOLE day is
   * still fetched. And the date strip reduces via `startOfDay()`. So the
   * time rides along as an ANCHOR for which slots to surface first
   * (time-slots-section), and changes no fetch and no date display.
   *
   * Still deliberately NOT a slot: this is a hint about which real slots to
   * show, never an assertion that 10:00 is bookable.
   */
  prefill.selectedDate = draft.start;

  if (draft.recurrence) {
    const count = Math.max(1, draft.recurrence.count);
    // Same stepping the ghost rows draw (Weeks = +7d, Months = +28d so the
    // weekday holds) — endDate spans first → last drafted occurrence.
    const stepDays = draft.recurrence.period === 'Months' ? 28 : 7;
    const endDate = addMinutesISO(draft.start, (count - 1) * stepDays * 24 * 60);
    prefill.recurring = {
      startDate: draft.start,
      endDate,
      repeatEvery: 1,
      numberOfRepeat: count,
      period: draft.recurrence.period,
      // `days` is only sent for weekly recurrences (appointment-form.ts).
      ...(draft.recurrence.period === 'Weeks'
        ? { days: [draft.recurrence.weekday] }
        : {}),
    };
  }

  return prefill;
}
